import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { CheckCircle, AlertTriangle, Loader2, Trash2, RefreshCw } from 'lucide-react';

const API = '/api';

export default function EmailTrackingPage() {
  const [bouncedList, setBouncedList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [purging, setPurging] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/email-tracking/recent-events?status=bounced`, { withCredentials: true });
      setBouncedList(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error('Failed to load bounced emails');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/email-tracking/sync`, {}, { withCredentials: true });
      const taskId = res.data.task_id;
      toast.info('Checking for new bounces...', { id: 'sync' });

      await new Promise((resolve) => {
        const interval = setInterval(async () => {
          try {
            const st = await axios.get(`${API}/email-tracking/sync-status/${taskId}`, { withCredentials: true });
            if (st.data.message && st.data.status === 'running') toast.info(st.data.message, { id: 'sync' });
            if (st.data.status === 'completed') { clearInterval(interval); toast.success(st.data.message || 'Done', { id: 'sync' }); resolve(); }
            if (st.data.status === 'failed') { clearInterval(interval); toast.error(st.data.message || 'Failed', { id: 'sync' }); resolve(); }
          } catch { clearInterval(interval); resolve(); }
        }, 3000);
      });

      fetchData();
    } catch {
      toast.error('Sync failed');
    } finally { setSyncing(false); }
  };

  const handlePurgeAll = async () => {
    setPurging(true);
    try {
      const res = await axios.post(`${API}/email-tracking/purge-bounced`, {}, { withCredentials: true });
      const d = res.data;
      toast.success(`Deleted ${d.total_contacts_removed} contacts and ${d.total_records_removed} records from the system`);
      setDeleteConfirm(null);
      fetchData();
    } catch { toast.error('Purge failed'); }
    finally { setPurging(false); }
  };

  const handleDeleteSingle = async () => {
    if (!deleteConfirm) return;
    setPurging(true);
    try {
      await axios.post(`${API}/email-tracking/mark-invalid`, { email: deleteConfirm.contact_email }, { withCredentials: true });
      if (deleteConfirm.id) {
        await axios.delete(`${API}/email-tracking/outreach/${deleteConfirm.id}`, { withCredentials: true });
      }
      toast.success(`${deleteConfirm.contact_email} deleted`);
      setDeleteConfirm(null);
      fetchData();
    } catch { toast.error('Failed to remove'); }
    finally { setPurging(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="email-tracking-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Bounced Emails
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">Emails that failed to deliver. Delete to clean your contact lists.</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="text-xs h-8"
          onClick={handleSync}
          disabled={syncing}
          data-testid="sync-btn"
        >
          {syncing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
          {syncing ? 'Checking...' : 'Check for Bounces'}
        </Button>
      </div>

      {/* Bounced List */}
      <Card data-testid="bounced-list-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <AlertTriangle className="w-4 h-4 text-red-500" />
              {bouncedList.length > 0 ? (
                <>
                  {bouncedList.length} Bounced
                  <Badge variant="secondary" className="text-[10px] h-5 bg-red-50 text-red-700">Action needed</Badge>
                </>
              ) : 'All Clear'}
            </CardTitle>
            {bouncedList.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                className="text-xs h-7 text-red-600 border-red-200 hover:bg-red-50"
                onClick={() => setDeleteConfirm({ type: 'all' })}
                data-testid="purge-all-bounced-btn"
              >
                <Trash2 className="w-3 h-3 mr-1" /> Delete All
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {bouncedList.length === 0 ? (
            <div className="text-center py-10">
              <CheckCircle className="w-8 h-8 text-green-400 mx-auto mb-3" />
              <p className="text-sm text-slate-600 font-medium">No bounced emails</p>
              <p className="text-xs text-slate-400 mt-1">Your contact list is clean. New bounces will appear here automatically.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 py-2">Contact</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 py-2">Company</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 py-2">Date</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 py-2 text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bouncedList.map((b, i) => (
                  <TableRow key={b.id || i} data-testid={`bounced-row-${i}`}>
                    <TableCell className="py-2">
                      <p className="text-xs font-medium text-slate-800">{b.contact_name || '—'}</p>
                      <p className="text-[10px] text-red-500">{b.contact_email}</p>
                    </TableCell>
                    <TableCell className="text-xs text-slate-600 py-2">{b.company_name || '—'}</TableCell>
                    <TableCell className="text-[10px] text-slate-400 py-2 whitespace-nowrap">
                      {b.created_at ? new Date(b.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                    </TableCell>
                    <TableCell className="py-2 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-[10px] h-6 px-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setDeleteConfirm({ ...b, type: 'single' })}
                        data-testid={`remove-bounced-${i}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={open => { if (!open) setDeleteConfirm(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              {deleteConfirm?.type === 'all'
                ? `Delete all ${bouncedList.length} bounced contacts?`
                : `Delete ${deleteConfirm?.contact_email}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteConfirm?.type === 'all'
                ? 'This permanently removes all bounced contacts from every list in the system. They will never receive emails again.'
                : `This permanently removes ${deleteConfirm?.contact_email} from all contact lists and outreach records.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteConfirm?.type === 'all' ? handlePurgeAll : handleDeleteSingle}
              className="bg-red-600 text-white hover:bg-red-700"
              disabled={purging}
              data-testid="confirm-delete"
            >
              {purging ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
